import { memo, useCallback } from 'react'
import { X } from 'lucide-react'
import { useTheme } from '../../../../theme/hooks'
import type {
  LidarProgressState,
  LidarUploadQueueItem,
} from '../../../../Contexts/LidarUploadContext'

type Props = {
  progressState: LidarProgressState
  uploadQueue: LidarUploadQueueItem[]
  maxQueue: number
  effectiveUploading: boolean
  onRemoveFromQueue?: (uploadId: string) => void
}

type QueueRowProps = {
  item: LidarUploadQueueItem
  onRemove?: (uploadId: string) => void
  textPrimary: string
  textSecondary: string
  hoverBg: string
}

const QueueRowBase = ({
  item,
  onRemove,
  textPrimary,
  textSecondary,
  hoverBg,
}: QueueRowProps) => {
  const handleClick = useCallback(() => {
    if (item.uploadId) onRemove?.(item.uploadId)
  }, [item.uploadId, onRemove])

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="min-w-0 truncate" style={{ color: textPrimary }}>
        {item.fileName}
      </span>
      <div className="flex shrink-0 items-center gap-2">
        <span style={{ color: textSecondary }}>En attente</span>
        {onRemove && (
          <button
            type="button"
            onClick={handleClick}
            className="rounded p-0.5 transition-colors hover:opacity-100"
            style={{ color: textSecondary }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = hoverBg
              e.currentTarget.style.color = textPrimary
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = textSecondary
            }}
            title="Retirer de la file"
            aria-label={`Retirer ${item.fileName} de la file d'attente`}
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  )
}

const QueueRow = memo(QueueRowBase)

const PROGRESS_GRADIENT =
  'linear-gradient(to right, #2563eb, #06b6d4, #059669)'

const ImportProgressPanelBase = ({
  progressState,
  uploadQueue,
  maxQueue,
  effectiveUploading,
  onRemoveFromQueue,
}: Props) => {
  const { theme } = useTheme()

  const showProgress = effectiveUploading && !!progressState.fileName
  const queuedItems = uploadQueue.slice(1)

  if (!showProgress && queuedItems.length === 0) return null

  return (
    <>
      {showProgress && (
        <div
          className="mt-4 rounded-md border p-3"
          style={{
            borderColor: theme.section.border,
            backgroundColor: theme.section.bgSubtle,
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <div
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: theme.text.tertiary }}
            >
              Import en cours
            </div>
            <span
              className="text-[11px] font-semibold"
              style={{ color: theme.text.primary }}
            >
              {Math.round(progressState.percent)}%
            </span>
          </div>
          <div
            className="mt-2 truncate text-sm font-medium"
            style={{ color: theme.text.primary }}
            title={progressState.fileName}
          >
            {progressState.fileName}
          </div>
          <div className="mt-1 text-xs" style={{ color: theme.text.secondary }}>
            {progressState.status || 'Traitement en cours…'}
          </div>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full"
            style={{ backgroundColor: theme.section.border }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(progressState.percent, 100)}%`,
                background: PROGRESS_GRADIENT,
              }}
            />
          </div>
        </div>
      )}

      {queuedItems.length > 0 && (
        <div
          className="mt-3 rounded-md border p-3 text-xs"
          style={{
            borderColor: theme.section.border,
            backgroundColor: theme.section.bgSubtle,
          }}
        >
          <div
            className="text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: theme.text.tertiary }}
          >
            File d&apos;attente (max {maxQueue})
          </div>
          <div className="mt-2 space-y-1">
            {queuedItems.map((item, index) => (
              <QueueRow
                key={item.uploadId || `${item.fileName}-${index}`}
                item={item}
                onRemove={onRemoveFromQueue}
                textPrimary={theme.text.primary}
                textSecondary={theme.text.secondary}
                hoverBg={theme.section.border}
              />
            ))}
          </div>
        </div>
      )}
    </>
  )
}

export const ImportProgressPanel = memo(ImportProgressPanelBase)
